import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Scale, History, AlertTriangle, Check } from 'lucide-react';
import { toast } from 'sonner';

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const sum = (weights, keys) =>
  keys.reduce((total, k) => total + (parseFloat(weights[k]) || 0), 0);

export const DimensionWeightsCard = () => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/assessments/dimension-weights`, { headers: headers() });
      setData(res.data);
      setDraft(res.data.current.weights);
    } catch {
      toast.error('Could not load dimension weights');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;

  const keys = data.dimensions.map((d) => d.key);
  const total = sum(draft, keys);
  const balanced = Math.abs(total - 100) < 0.0001;
  const dirty = keys.some((k) => String(draft[k]) !== String(data.current.weights[k]));

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/admin/assessments/dimension-weights`,
        { weights: keys.reduce((acc, k) => ({ ...acc, [k]: String(draft[k]) }), {}) },
        { headers: headers() },
      );
      setData((prev) => ({ ...prev, current: res.data.current, history: res.data.history }));
      setDraft(res.data.current.weights);
      toast.success(`Saved as version ${res.data.current.version}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save dimension weights');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-gray-900/60 border-gray-700" data-testid="dimension-weights-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Scale className="w-5 h-5 text-cyan-400" />
              Dimension Weights
            </h3>
            <p className="text-sm text-gray-400 mt-1 max-w-2xl">
              How much each dimension counts toward the overall score. Program-wide — every
              assessment uses the same six. The number of questions in a dimension does not
              affect its weight.
            </p>
          </div>
          <Badge variant="outline" className="text-gray-300 border-gray-600 whitespace-nowrap"
                 data-testid="dimension-weights-version">
            Version {data.current.version}
          </Badge>
        </div>

        <div className="mt-5 space-y-2">
          {data.dimensions.map((d) => (
            <div key={d.key} className="flex items-center gap-3">
              <label className="flex-1 text-sm text-gray-300" htmlFor={`weight-${d.key}`}>
                {d.name}
              </label>
              <input
                id={`weight-${d.key}`}
                data-testid={`dimension-weight-input-${d.key}`}
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={draft[d.key] ?? ''}
                onChange={(e) => setDraft({ ...draft, [d.key]: e.target.value })}
                className="w-24 bg-gray-800 border border-gray-600 rounded-md px-3 py-1.5 text-right
                           text-white focus:outline-none focus:border-cyan-400 transition-colors"
              />
              <span className="text-gray-500 text-sm w-4">%</span>
            </div>
          ))}
        </div>

        <div className={`mt-4 flex items-center gap-2 text-sm rounded-md px-3 py-2 ${
          balanced ? 'bg-emerald-500/10 text-emerald-300' : 'bg-amber-500/10 text-amber-300'
        }`} data-testid="dimension-weights-total">
          {balanced
            ? <><Check className="w-4 h-4" /> Totals 100% — ready to save</>
            : <><AlertTriangle className="w-4 h-4" /> Totals {Number(total.toFixed(2))}% — must be
                exactly 100% before this can be saved</>}
        </div>

        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <Button
            onClick={save}
            disabled={!dirty || !balanced || saving}
            data-testid="dimension-weights-save-btn"
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
          >
            {saving ? 'Saving…' : 'Save new version'}
          </Button>
          {dirty && (
            <button
              onClick={() => setDraft(data.current.weights)}
              data-testid="dimension-weights-reset-btn"
              className="text-sm text-gray-400 hover:text-gray-200 transition-colors"
            >
              Discard changes
            </button>
          )}
          <button
            onClick={() => setShowHistory((v) => !v)}
            data-testid="dimension-weights-history-toggle"
            className="ml-auto text-sm text-gray-400 hover:text-gray-200 flex items-center gap-1.5
                       transition-colors"
          >
            <History className="w-4 h-4" />
            {showHistory ? 'Hide' : 'Show'} history ({data.history.length})
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-3">
          Saving never changes a past result. Every attempt records the weight set it was scored
          against, so previous scores stay exactly as the member saw them.
        </p>

        {showHistory && (
          <div className="mt-4 border-t border-gray-700 pt-4 space-y-2"
               data-testid="dimension-weights-history">
            {data.history.map((set) => (
              <div key={set.id} className="text-xs text-gray-400 flex flex-wrap gap-x-3 gap-y-1">
                <span className="text-gray-300 font-medium w-16">v{set.version}</span>
                <span className="w-44">{new Date(set.created_at).toLocaleString()}</span>
                <span className="w-56 truncate">{set.created_by_email || '—'}</span>
                <span>{data.dimensions.map((d) => set.weights[d.key]).join(' / ')}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DimensionWeightsCard;
